import { Location } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, retry, throwError } from 'rxjs';
import { SnackbarService } from 'src/app/modules/shared/components/snack-bar/snackbar.service';
import { ErrorResponse } from 'src/app/modules/shared/viewmodels/error.response';
import { SUPPRESS_SERVER_ERROR_SNACKBAR } from './server-error.http-context';

export const serverErrorHttpInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const location = inject(Location);
  const snackbar = inject(SnackbarService);

  return next(req).pipe(
    retry(parseInt(req.headers.get('fit-retry-count')!) || 0),
    catchError((res: HttpErrorResponse) => {
      if (res.status === 400 || res.status === 500) {
        let errorMessage: string;

        if (res.error instanceof Blob) {
          // Handle Blob errors
          return from(
            new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.readAsText(res.error);
            }).then((text) => {
              const parsed: Partial<ErrorResponse> = JSON.parse(text || '{}');
              errorMessage = parsed.message || res.statusText || 'Internal server error occurred';
              displayError(
                errorMessage,
                res,
                router,
                location,
                snackbar,
                req.context.get(SUPPRESS_SERVER_ERROR_SNACKBAR)
              );
              throw res;
            })
          );
        } else if (typeof res.error === 'object') {
          // Handle JSON errors
          const error: Partial<ErrorResponse> = res.error;
          errorMessage = error?.message ?? JSON.stringify(res.error);
        } else {
          // Handle String errors
          errorMessage = res.error || res.statusText || 'Internal server error occurred';
        }

        displayError(
          errorMessage,
          res,
          router,
          location,
          snackbar,
          req.context.get(SUPPRESS_SERVER_ERROR_SNACKBAR)
        );
      }
      return throwError(() => res);
    })
  );
};

function displayError(
  errorMessage: string,
  res: HttpErrorResponse,
  router: Router,
  location: Location,
  snackbar: SnackbarService,
  suppressSnackbar: boolean
) {
  if (res.headers.get('fit-server-error-page') === 'true') {
    router.navigateByUrl('/error', { skipLocationChange: true });
    location.replaceState(router.url, '', { message: errorMessage });
  } else {
    console.error(res.error || errorMessage);
    if (!suppressSnackbar) {
      snackbar.error(errorMessage);
    }
  }
}
